import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Tab,
  Tabs,
  Badge,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Warning as WarningIcon,
  Inventory as InventoryIcon,
  LocalPharmacy as PharmacyIcon,
  Print as PrintIcon,
  Save as SaveIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import { Pharmacy, CreatePharmacyRequest } from '../types/Pharmacy';
import { MedicineReturn } from '../types/MedicineReturn';
import { pharmacyService } from '../services/pharmacyService';
import { medicineReturnService } from '../services/medicineReturnService';
import { formatCurrency, formatDate } from '../utils/formatters';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`pharmacy-tabpanel-${index}`}
      aria-labelledby={`pharmacy-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const PharmacyPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [pharmacyItems, setPharmacyItems] = useState<Pharmacy[]>([]);
  const [lowStockItems, setLowStockItems] = useState<Pharmacy[]>([]);
  const [expiringItems, setExpiringItems] = useState<Pharmacy[]>([]);
  const [medicineReturns, setMedicineReturns] = useState<MedicineReturn[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState<Pharmacy | null>(null);
  const [formData, setFormData] = useState<CreatePharmacyRequest>({
    medicineName: '',
    category: '',
    manufacturer: '',
    unitPrice: 0,
    costPrice: 0,
    stockQuantity: 0,
    unit: 'tablets',
    expiryDate: '',
    description: '',
    indications: '',
    sideEffects: '',
    dosage: '',
    hsnCode: '',
    batchNumber: '',
    packSize: '',
    discountPercentage: 0,
    isAvailable: true,
    stockAddedDate: '',
  });

  const categories = ['Pain Relief', 'Antibiotic', 'Vitamin', 'Supplement', 'Injection', 'Syrup', 'Capsule', 'Other'];
  const units = ['tablets', 'capsules', 'ml', 'mg', 'bottles', 'vials'];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  useEffect(() => {
    fetchPharmacyItems();
    fetchLowStockItems();
    fetchExpiringItems();
    fetchMedicineReturns();
  }, []);

  const fetchPharmacyItems = async () => {
    try {
      setLoading(true);
      const data = await pharmacyService.getAllPharmacyItems();
      setPharmacyItems(data);
    } catch (error) {
      console.error('Failed to fetch pharmacy items:', error);
      setError('Failed to load pharmacy items');
    } finally {
      setLoading(false);
    }
  };

  const fetchMedicineReturns = async () => {
    try {
      const data = await medicineReturnService.getAllReturns();
      setMedicineReturns(data);
    } catch (error) {
      console.error('Failed to fetch medicine returns:', error);
    }
  };

  const fetchLowStockItems = async () => {
    try {
      const data = await pharmacyService.getLowStockItems(10);
      setLowStockItems(data);
    } catch (error) {
      console.error('Failed to fetch low stock items:', error);
    }
  };

  const fetchExpiringItems = async () => {
    try {
      const data = await pharmacyService.getExpiringItems(30);
      setExpiringItems(data);
    } catch (error) {
      console.error('Failed to fetch expiring items:', error);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      fetchPharmacyItems();
      return;
    }

    try {
      setLoading(true);
      const data = await pharmacyService.searchPharmacyItems(searchTerm);
      setPharmacyItems(data);
    } catch (error) {
      console.error('Failed to search pharmacy items:', error);
      setError('Failed to search pharmacy items');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (item?: Pharmacy) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        medicineName: item.medicineName,
        category: item.category,
        manufacturer: item.manufacturer,
        unitPrice: item.unitPrice,
        costPrice: item.costPrice || 0,
        stockQuantity: item.stockQuantity,
        unit: item.unit,
        expiryDate: item.expiryDate ? item.expiryDate.split('T')[0] : '',
        description: item.description,
        indications: item.indications,
        sideEffects: item.sideEffects,
        dosage: item.dosage,
        hsnCode: item.hsnCode || '',
        batchNumber: item.batchNumber || '',
        packSize: item.packSize || '',
        discountPercentage: item.discountPercentage || 0,
        isAvailable: item.isAvailable,
        stockAddedDate: item.stockAddedDate ? item.stockAddedDate.split('T')[0] : '',
      });
    } else {
      setEditingItem(null);
      setFormData({
        medicineName: '',
        category: '',
        manufacturer: '',
        unitPrice: 0,
        costPrice: 0,
        stockQuantity: 0,
        unit: 'tablets',
        expiryDate: '',
        description: '',
        indications: '',
        sideEffects: '',
        dosage: '',
        hsnCode: '',
        batchNumber: '',
        packSize: '',
        discountPercentage: 0,
        isAvailable: true,
        stockAddedDate: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    setError('');
  };

  const handleSubmit = async () => {
    try {
      if (editingItem) {
        await pharmacyService.updatePharmacyItem(editingItem.pharmacyId, formData);
      } else {
        await pharmacyService.createPharmacyItem(formData);
      }
      handleCloseDialog();
      fetchPharmacyItems();
      fetchLowStockItems();
      fetchExpiringItems();
    } catch (error: any) {
      console.error('Failed to save pharmacy item:', error);
      setError(error.response?.data?.message || 'Failed to save pharmacy item');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this pharmacy item?')) {
      try {
        await pharmacyService.deletePharmacyItem(id);
        fetchPharmacyItems();
        fetchLowStockItems();
        fetchExpiringItems();
      } catch (error: any) {
        console.error('Failed to delete pharmacy item:', error);
        setError(error.response?.data?.message || 'Failed to delete pharmacy item');
      }
    }
  };

  // Medicine Return Handlers
  const handleApproveReturn = async (returnId: number) => {
    try {
      await medicineReturnService.approveReturn(returnId);
      fetchMedicineReturns();
    } catch (error: any) {
      console.error('Failed to approve return:', error);
      setError(error.response?.data?.message || 'Failed to approve return');
    }
  };

  const handleRejectReturn = async (returnId: number) => {
    try {
      await medicineReturnService.rejectReturn(returnId);
      fetchMedicineReturns();
    } catch (error: any) {
      console.error('Failed to reject return:', error);
      setError(error.response?.data?.message || 'Failed to reject return');
    }
  };

  const handleCompleteReturn = async (returnId: number) => {
    try {
      await medicineReturnService.completeReturn(returnId);
      fetchMedicineReturns();
      fetchPharmacyItems(); // Refresh stock as it was updated
    } catch (error: any) {
      console.error('Failed to complete return:', error);
      setError(error.response?.data?.message || 'Failed to complete return');
    }
  };

  const getReturnStatusChip = (status: string) => {
    const statusConfig: { [key: string]: { color: 'warning' | 'info' | 'success' | 'error'; label: string } } = {
      'Pending': { color: 'warning', label: 'Pending' },
      'Approved': { color: 'info', label: 'Approved' },
      'Completed': { color: 'success', label: 'Completed' },
      'Rejected': { color: 'error', label: 'Rejected' },
    };
    const config = statusConfig[status] || { color: 'warning' as const, label: status };
    return <Chip size="small" color={config.color} label={config.label} />;
  };

  const printMedicineDetails = (medicine: Pharmacy) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const printContent = `
      <html>
        <head>
          <title>Medicine Details - ${medicine.medicineName}</title>
          <style>
            body {
              font-family: 'Arial', sans-serif;
              margin: 0;
              padding: 20px;
              background-color: #fff;
              color: #333;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 3px solid #2C7A9C;
              padding-bottom: 20px;
            }
            .header-content {
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 20px;
            }
            .logo-img {
              width: 80px;
              height: 80px;
              object-fit: contain;
            }
            .header-text {
              text-align: left;
            }
            .clinic-name {
              color: #2C7A9C;
              font-size: 24px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            .doctor-name {
              color: #1E5A73;
              font-size: 18px;
              font-weight: 600;
              margin-bottom: 3px;
            }
            .subtitle {
              color: #666;
              font-size: 12px;
            }
            .medicine-card {
              border: 2px solid #e0e7ff;
              border-radius: 12px;
              padding: 25px;
              margin: 20px 0;
              background: linear-gradient(135deg, #f8fafc 0%, #e0f7ff 100%);
            }
            .medicine-title {
              color: #2C7A9C;
              font-size: 24px;
              font-weight: bold;
              margin-bottom: 15px;
              text-align: center;
            }
            .detail-row {
              display: flex;
              justify-content: space-between;
              margin: 10px 0;
              padding: 8px;
              background-color: rgba(255, 255, 255, 0.7);
              border-radius: 6px;
            }
            .label {
              font-weight: bold;
              color: #374151;
              width: 30%;
            }
            .value {
              color: #059669;
              font-weight: 600;
              width: 65%;
              text-align: right;
            }
            .status {
              text-align: center;
              padding: 15px;
              margin: 20px 0;
              border-radius: 8px;
              font-weight: bold;
              font-size: 16px;
            }
            .status.available {
              background-color: #d1fae5;
              color: #065f46;
              border: 2px solid #059669;
            }
            .status.unavailable {
              background-color: #fee2e2;
              color: #991b1b;
              border: 2px solid #dc2626;
            }
            .footer {
              margin-top: 40px;
              text-align: center;
              color: #6b7280;
              font-size: 12px;
              border-top: 1px solid #e5e7eb;
              padding-top: 20px;
            }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="header-content">
              <img src="/images/plasticare-logo.png" alt="Plasticare Logo" class="logo-img" />
              <div class="header-text">
                <div class="clinic-name">SCULPTE by Plasticare</div>
                <div class="doctor-name">Dr. Puneet Kumar Bharadwaj</div>
                <div class="subtitle">Plastic & Cosmetic Surgery | Hair Transplant</div>
              </div>
            </div>
          </div>
          
          <div class="medicine-card">
            <div class="medicine-title">${medicine.medicineName}</div>
            
            <div class="detail-row">
              <span class="label">Medicine ID:</span>
              <span class="value">#MED${String(medicine.pharmacyId).padStart(4, '0')}</span>
            </div>
            
            <div class="detail-row">
              <span class="label">Category:</span>
              <span class="value">${medicine.category}</span>
            </div>
            
            <div class="detail-row">
              <span class="label">Manufacturer:</span>
              <span class="value">${medicine.manufacturer}</span>
            </div>
            
            <div class="detail-row">
              <span class="label">Dosage:</span>
              <span class="value">${medicine.dosage}</span>
            </div>
            
            <div class="detail-row">
              <span class="label">Unit Price:</span>
              <span class="value">${formatCurrency(medicine.unitPrice)} per ${medicine.unit}</span>
            </div>
            
            <div class="detail-row">
              <span class="label">Stock Quantity:</span>
              <span class="value">${medicine.stockQuantity} ${medicine.unit}${medicine.stockQuantity !== 1 ? 's' : ''}</span>
            </div>
            
            <div class="detail-row">
              <span class="label">Expiry Date:</span>
              <span class="value">${medicine.expiryDate ? formatDate(medicine.expiryDate) : 'Not specified'}</span>
            </div>
            
            <div class="detail-row">
              <span class="label">Description:</span>
              <span class="value">${medicine.description || 'No description available'}</span>
            </div>
            
            <div class="status ${medicine.isAvailable ? 'available' : 'unavailable'}">
              Status: ${medicine.isAvailable ? 'Available for Prescription' : 'Currently Unavailable'}
            </div>
          </div>
          
          <div class="footer">
            <p>Printed on: ${new Date().toLocaleDateString('en-IN', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}</p>
            <p>This is a computer-generated document.</p>
            <p style="margin-top: 10px; color: #2C7A9C;"><strong>Thank you for choosing SCULPTE by Plasticare!</strong></p>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const handleInputChange = (field: keyof CreatePharmacyRequest) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
  ) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getExpiryChip = (expiryDate: string | null) => {
    if (!expiryDate) {
      return <Chip label="No Expiry" color="default" size="small" />;
    }
    const today = new Date();
    const expiry = new Date(expiryDate);
    const daysUntilExpiry = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 3600 * 24));

    if (daysUntilExpiry < 0) {
      return <Chip label="Expired" color="error" size="small" />;
    } else if (daysUntilExpiry <= 30) {
      return <Chip label={`Expires in ${daysUntilExpiry} days`} color="warning" size="small" />;
    } else {
      return <Chip label={formatDate(expiryDate)} color="success" size="small" />;
    }
  };

  const getStockChip = (quantity: number) => {
    if (quantity === 0) {
      return <Chip label="Out of Stock" color="error" size="small" />;
    } else if (quantity <= 10) {
      return <Chip label={`Low Stock (${quantity})`} color="warning" size="small" />;
    } else {
      return <Chip label={`${quantity} in stock`} color="success" size="small" />;
    }
  };

  const renderPharmacyTable = (items: Pharmacy[]) => (
    <TableContainer 
      component={Paper}
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
      }}
    >
      <Table>
        <TableHead>
          <TableRow sx={{ background: 'linear-gradient(135deg, #2C7A9C 0%, #1E5A73 100%)' }}>
            <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Medicine Name</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Category</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Manufacturer</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Unit Price</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Stock</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Stock Added</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Expiry</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Status</TableCell>
            <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item, index) => (
            <TableRow 
              key={item.pharmacyId}
              sx={{
                backgroundColor: index % 2 === 0 ? '#f8fafc' : 'white',
                '&:hover': {
                  backgroundColor: '#e8f4f8',
                },
              }}
            >
              <TableCell sx={{ py: 2 }}>
                <Box>
                  <Typography variant="body1" fontWeight="bold" sx={{ color: '#1E5A73' }}>
                    {item.medicineName}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    {item.dosage}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell sx={{ py: 2 }}>
                <Chip 
                  label={item.category} 
                  size="small" 
                  sx={{
                    backgroundColor: '#c5dfe8',
                    color: '#1E5A73',
                    fontWeight: 600,
                  }}
                />
              </TableCell>
              <TableCell sx={{ py: 2, color: '#374151' }}>{item.manufacturer}</TableCell>
              <TableCell sx={{ py: 2 }}>
                <Typography variant="body2" fontWeight="bold" sx={{ color: '#059669' }}>
                  {formatCurrency(item.unitPrice)} / {item.unit}
                </Typography>
              </TableCell>
              <TableCell sx={{ py: 2 }}>{getStockChip(item.stockQuantity)}</TableCell>
              <TableCell sx={{ py: 2 }}>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  {item.stockAddedDate ? formatDate(item.stockAddedDate) : '-'}
                </Typography>
              </TableCell>
              <TableCell sx={{ py: 2 }}>{getExpiryChip(item.expiryDate)}</TableCell>
              <TableCell sx={{ py: 2 }}>
                <Chip
                  label={item.isAvailable ? 'Available' : 'Unavailable'}
                  color={item.isAvailable ? 'success' : 'error'}
                  size="small"
                />
              </TableCell>
              <TableCell sx={{ py: 2 }}>
                <Tooltip title="Edit">
                  <IconButton
                    sx={{ 
                      color: '#2C7A9C',
                      '&:hover': { backgroundColor: '#c5dfe8' },
                    }}
                    size="small"
                    onClick={() => handleOpenDialog(item)}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Print Details">
                  <IconButton
                    sx={{ 
                      color: '#059669',
                      '&:hover': { backgroundColor: '#ecfdf5' },
                      mx: 0.5,
                    }}
                    size="small"
                    onClick={() => printMedicineDetails(item)}
                  >
                    <PrintIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    sx={{ 
                      color: '#dc2626',
                      '&:hover': { backgroundColor: '#fee2e2' },
                    }}
                    size="small"
                    onClick={() => handleDelete(item.pharmacyId)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
        sx={{ 
          background: 'linear-gradient(135deg, #c5dfe8 0%, #e8f4f8 100%)',
          borderRadius: 3,
          m: 2
        }}
      >
        <CircularProgress sx={{ color: '#2C7A9C' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 3, 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #e8f4f8 0%, #c5dfe8 100%)',
      backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)',
      backgroundSize: '20px 20px'
    }}>
      {/* Header Card */}
      <Card 
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #2C7A9C 0%, #1E5A73 100%)',
          color: 'white',
          mb: 4,
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <PharmacyIcon sx={{ fontSize: 48, color: 'white' }} />
              <Box>
                <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                  Pharmacy Management
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, color: 'white' }}>
                  Manage medicines, track inventory, and monitor stock levels
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.3)',
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                borderRadius: 2,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.3)',
                }
              }}
            >
              Add Medicine
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box 
        sx={{ 
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          borderRadius: 2,
          p: 2,
          mb: 3,
          border: '1px solid #e2e8f0',
        }}
      >
        <Tabs 
          value={tabValue} 
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{
            '& .MuiTab-root': {
              fontWeight: 600,
              textTransform: 'none',
              color: '#64748b',
              '&.Mui-selected': {
                color: '#2C7A9C',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#2C7A9C',
              height: 3,
            },
          }}
        >
          <Tab label="All Medicines" />
          <Tab 
            label={
              <Badge badgeContent={lowStockItems.length} color="warning">
                Low Stock
              </Badge>
            }
          />
          <Tab 
            label={
              <Badge badgeContent={expiringItems.length} color="error">
                Expiring Soon
              </Badge>
            }
          />
          <Tab 
            label={
              <Badge badgeContent={medicineReturns.filter(r => r.status === 'Pending' || r.status === 'Approved').length} color="info">
                Returns
              </Badge>
            }
          />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField
            label="Search medicines..."
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ flexGrow: 1 }}
          />
          <Button
            variant="outlined"
            startIcon={<SearchIcon />}
            onClick={handleSearch}
          >
            Search
          </Button>
        </Box>

        {renderPharmacyTable(pharmacyItems)}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Typography variant="h6" gutterBottom color="warning.main">
          <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Low Stock Items (10 or less)
        </Typography>
        {renderPharmacyTable(lowStockItems)}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Typography variant="h6" gutterBottom color="error.main">
          <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Items Expiring in 30 Days
        </Typography>
        {renderPharmacyTable(expiringItems)}
      </TabPanel>

      {/* Medicine Returns Tab */}
      <TabPanel value={tabValue} index={3}>
        <Typography variant="h6" gutterBottom sx={{ color: '#2C7A9C' }}>
          <UndoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Medicine Returns
        </Typography>
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ background: 'linear-gradient(135deg, #2C7A9C 0%, #1E5A73 100%)' }}>
                <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Return ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Patient</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Medicine</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Qty</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Refund</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Reason</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Date</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 700, py: 2 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {medicineReturns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#6b7280' }}>
                    No medicine returns found
                  </TableCell>
                </TableRow>
              ) : (
                medicineReturns.map((ret, index) => (
                  <TableRow 
                    key={ret.returnId}
                    sx={{
                      backgroundColor: index % 2 === 0 ? '#f8fafc' : 'white',
                      '&:hover': { backgroundColor: '#f0f9ff' },
                    }}
                  >
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: '#1E5A73' }}>
                        #{ret.returnId}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2, color: '#374151' }}>{ret.patientName || 'N/A'}</TableCell>
                    <TableCell sx={{ py: 2, color: '#374151' }}>{ret.medicineName}</TableCell>
                    <TableCell sx={{ py: 2, color: '#374151' }}>{ret.quantity}</TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: '#059669' }}>
                        {formatCurrency(ret.totalRefund)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2, color: '#374151' }}>{ret.reason}</TableCell>
                    <TableCell sx={{ py: 2, color: '#374151' }}>{formatDate(ret.returnDate)}</TableCell>
                    <TableCell sx={{ py: 2 }}>{getReturnStatusChip(ret.status)}</TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Stack direction="row" spacing={1}>
                        {ret.status === 'Pending' && (
                          <>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                              onClick={() => handleApproveReturn(ret.returnId)}
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                              onClick={() => handleRejectReturn(ret.returnId)}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                        {ret.status === 'Approved' && (
                          <Button
                            size="small"
                            variant="contained"
                            sx={{
                              background: 'linear-gradient(135deg, #2C7A9C 0%, #1E5A73 100%)',
                              color: 'white',
                              textTransform: 'none',
                              fontWeight: 600,
                              borderRadius: 2,
                            }}
                            onClick={() => handleCompleteReturn(ret.returnId)}
                          >
                            Complete & Restock
                          </Button>
                        )}
                        {ret.status === 'Completed' && (
                          <Typography variant="caption" color="success.main">
                            Stock Updated
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit Medicine' : 'Add New Medicine'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Medicine Name"
                value={formData.medicineName}
                onChange={handleInputChange('medicineName')}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  onChange={handleInputChange('category')}
                >
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Manufacturer"
                value={formData.manufacturer}
                onChange={handleInputChange('manufacturer')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Unit Price (Selling Price)"
                type="number"
                value={formData.unitPrice}
                onChange={handleInputChange('unitPrice')}
                required
                inputProps={{ step: 0.01, min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cost Price (Purchase Price)"
                type="number"
                value={formData.costPrice}
                onChange={handleInputChange('costPrice')}
                inputProps={{ step: 0.01, min: 0 }}
                helperText="Purchase price for profit calculation"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Stock Quantity"
                type="number"
                value={formData.stockQuantity}
                onChange={handleInputChange('stockQuantity')}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Unit</InputLabel>
                <Select
                  value={formData.unit}
                  onChange={handleInputChange('unit')}
                >
                  {units.map((unit) => (
                    <MenuItem key={unit} value={unit}>
                      {unit}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Expiry Date"
                type="date"
                value={formData.expiryDate}
                onChange={handleInputChange('expiryDate')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Stock Added Date"
                type="date"
                value={formData.stockAddedDate}
                onChange={handleInputChange('stockAddedDate')}
                InputLabelProps={{ shrink: true }}
                helperText="Date when stock was added/restocked"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Dosage"
                value={formData.dosage}
                onChange={handleInputChange('dosage')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="HSN Code"
                value={formData.hsnCode}
                onChange={handleInputChange('hsnCode')}
                placeholder="e.g., 30049099"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Batch Number"
                value={formData.batchNumber}
                onChange={handleInputChange('batchNumber')}
                placeholder="e.g., C092502"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Pack Size"
                value={formData.packSize}
                onChange={handleInputChange('packSize')}
                placeholder="e.g., 1*10, 1*5"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Discount %"
                type="number"
                value={formData.discountPercentage}
                onChange={handleInputChange('discountPercentage')}
                placeholder="e.g., 10"
                inputProps={{ step: 0.01, min: 0, max: 100 }}
                helperText="Default discount when added to bill"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={formData.description}
                onChange={handleInputChange('description')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Indications"
                multiline
                rows={2}
                value={formData.indications}
                onChange={handleInputChange('indications')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Side Effects"
                multiline
                rows={2}
                value={formData.sideEffects}
                onChange={handleInputChange('sideEffects')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingItem ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PharmacyPage;